// src/components/auth/SignUpForm.js
// Updated: 06/20/2026

import React, { useState, useEffect } from 'react'; 
import { useNavigate, useSearchParams } from 'react-router-dom'; 
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth'; 
import { doc, setDoc } from 'firebase/firestore';
import { auth, db, functions } from '../../firebase/config'; 
import { httpsCallable } from 'firebase/functions'; 
import toast from 'react-hot-toast';
import Spinner from '../common/Spinner';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

// Initialize the callable function
const signUpAndJoinFamilyCallable = httpsCallable(functions, 'signUpAndJoinFamily');

const SignUpForm = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // Hook to get URL params
  const [inviteCode, setInviteCode] = useState(null); // State to store invite code

  // Effect to read invite code from URL on component mount
  useEffect(() => {
    const code = searchParams.get('inviteCode');
    if (code) {
      setInviteCode(code);
      // Optional: Display a message indicating they are joining via invite
      toast(`Joining family with invite code: ${code}`);
    }
  }, [searchParams]);

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match. Please try again.');
      return;
    }

    setLoading(true);

    try {
      if (inviteCode) {
        // --- NEW LOGIC: Use Cloud Function if invite code exists ---
        const result = await signUpAndJoinFamilyCallable({
          email,
          password,
          firstName,
          lastName,
          inviteCode,
        });

        // Sign in the user locally after successful creation by the function
        await signInWithEmailAndPassword(auth, email, password);

        toast.success(`Account created and joined '${result.data.familyName}' family!`);
        navigate('/'); // Redirect directly to homepage

      } else {
        // --- EXISTING LOGIC: Standard signup without invite code ---
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, 'users', user.uid), {
          firstName,
          lastName,
          email,
          profilePictureUrl: '',
          familyId: null, // User has not joined or created a family yet
        });

        toast.success('Account created! Please continue to onboarding.');
        navigate('/onboarding'); // Redirect to onboarding flow
      }
    } catch (error) {
        // Use error.message which often contains useful info from HttpsError
        toast.error(error.message || 'Sign up failed. Please try again.');
        console.error("Sign up error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignUp} className="space-y-6" autoComplete="off">
       <div className="flex gap-4">
        <div className="w-1/2">
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            First name
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            autoComplete="off"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
          />
        </div>
        <div className="w-1/2">
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            Last name
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            autoComplete="off"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
          />
        </div>
      </div>

      <div>
        <label htmlFor="email-signup" className="block text-sm font-medium text-gray-700">
          Email address
        </label>
        <input
          id="email-signup"
          name="email"
          type="email"
          autoComplete="off"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
        />
      </div>

      <div>
        <label htmlFor="password-signup" className="block text-sm font-medium text-gray-700">
          Password
        </label>
        <div className="mt-1 relative">
            <input
            id="password-signup"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            minLength="6"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
            />
            <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
            >
                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
        </div>
      </div>

      <div>
        <label htmlFor="confirm-password-signup" className="block text-sm font-medium text-gray-700">
          Confirm Password
        </label>
        <div className="mt-1 relative">
            <input
            id="confirm-password-signup"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            minLength="6"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
            />
             <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
            >
                {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
        >
          {loading ? <Spinner size="sm" /> : 'Create account'}
        </button>
      </div>
    </form>
  );
};

export default SignUpForm;